import { AlertCircle, AlertTriangle, Check } from "lucide-react";
import { EquipmentSelect } from "@/components/ui/equipment-select";
import { FieldInput } from "@/components/post-load/PostLoadCard";
import {
  ToggleChipRow,
  ColorChipRow,
  EQUIPMENT_CHIPS,
  LENGTH_CHIPS,
  WEIGHT_CHIPS,
} from "@/components/post-load/EquipmentDetailsCard";
import type { WizardFields } from "./wizardTypes";

/**
 * Guided wizard — STEP 4: EQUIPMENT & WEIGHT.
 *
 * Reuses the form's field kit verbatim: EquipmentSelect + Van/Reefer/Flatbed
 * quick chips, the colored Capacity (FTL/LTL) and Market (Spot/Dedicated/
 * Contract) chip rows, length/weight chips, and the >45,000 lb warning.
 * Equipment pre-fills from the shipper's saved default (applied by the page).
 */

interface WizardEquipmentStepProps {
  fields: WizardFields;
  onChange: (patch: Partial<WizardFields>) => void;
  error?: string | null;
  onSaveEquipmentDefault: () => void;
  savingEquipmentDefault?: boolean;
  equipmentDefaultSaved?: boolean;
}

const CAPACITY_OPTIONS = [
  { value: "FTL", label: "Full" },
  { value: "LTL", label: "Partial" },
];

const MARKET_OPTIONS = [
  { value: "spot", label: "Spot" },
  { value: "dedicated", label: "Dedicated" },
  { value: "contract", label: "Contract" },
];

export default function WizardEquipmentStep({
  fields,
  onChange,
  error,
  onSaveEquipmentDefault,
  savingEquipmentDefault = false,
  equipmentDefaultSaved = false,
}: WizardEquipmentStepProps) {
  const weightNum = parseInt(fields.weight.replace(/\D/g, ""), 10);
  const showWeightWarning = !isNaN(weightNum) && weightNum > 45000;

  const setEquipment = (v: string) => onChange({ equipmentType: v });

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2.5">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-destructive" />
          <p className="text-xs text-destructive">{error}</p>
        </div>
      )}

      {/* Capacity + market type — the form's colored chips */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ColorChipRow
          label="Capacity"
          options={CAPACITY_OPTIONS}
          value={fields.loadType}
          onChange={(v) => onChange({ loadType: v as WizardFields["loadType"] })}
        />
        <ColorChipRow
          label="Market Type"
          options={MARKET_OPTIONS}
          value={fields.loadNature}
          onChange={(v) => onChange({ loadNature: v as WizardFields["loadNature"] })}
        />
      </div>

      {/* Equipment type */}
      <div>
        <label className="text-[11px] font-medium text-muted-foreground block mb-1">
          Equipment type <span className="text-destructive">*</span>
        </label>
        <EquipmentSelect
          value={fields.equipmentType}
          onChange={(val) => setEquipment(val as string)}
          mode="single"
          placeholder="Equipment Type"
          className="h-10 text-sm"
        />
        <div className="flex items-center gap-2 flex-wrap mt-1.5">
          <ToggleChipRow
            chips={EQUIPMENT_CHIPS}
            currentValue={fields.equipmentType}
            onSelect={setEquipment}
            onClear={() => setEquipment("")}
          />
          <button
            type="button"
            disabled={!fields.equipmentType || savingEquipmentDefault}
            onClick={onSaveEquipmentDefault}
            className="text-[10px] font-semibold underline underline-offset-2 disabled:opacity-40 disabled:no-underline"
            style={{ color: "hsl(var(--brand-teal-deep))" }}
          >
            {savingEquipmentDefault ? "Saving..." : "Save as default"}
          </button>
          {equipmentDefaultSaved && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-medium"
              style={{ color: "hsl(var(--brand-teal-deep))" }}
            >
              <Check className="h-3 w-3" />
              Saved
            </span>
          )}
        </div>
      </div>

      {/* Weight + length — same input guards and quick chips as the form */}
      <div className="grid grid-cols-2 gap-3 items-start">
        <div className="flex flex-col">
          <FieldInput
            label="Weight (lbs.)"
            required
            value={fields.weight}
            onChange={(v) => {
              if (v === "" || /^\d+$/.test(v)) {
                const n = parseInt(v);
                if (v === "" || n <= 99999) onChange({ weight: v });
              }
            }}
          />
          <div className="mt-1.5">
            <ToggleChipRow
              chips={WEIGHT_CHIPS}
              currentValue={fields.weight}
              onSelect={(v) => onChange({ weight: v })}
              onClear={() => onChange({ weight: "" })}
            />
          </div>
        </div>
        <div className="flex flex-col">
          {/* Invisible label spacer: keeps this input level with the required
              "Weight" field, which renders a visible label above its input. */}
          <span aria-hidden className="text-[10px] font-medium mb-1 block invisible select-none">
            Length (ft)
          </span>
          <FieldInput
            label="Length (ft)"
            value={fields.length}
            onChange={(v) => {
              if (v === "" || /^\d*\.?\d*$/.test(v)) {
                const n = parseFloat(v);
                if (v === "" || n <= 99) onChange({ length: v });
              }
            }}
          />
          <div className="mt-1.5">
            <ToggleChipRow
              chips={LENGTH_CHIPS}
              currentValue={fields.length}
              onSelect={(v) => onChange({ length: v })}
              onClear={() => onChange({ length: "" })}
            />
          </div>
        </div>
      </div>


      {showWeightWarning && (
        <div
          className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[10px]"
          style={{ backgroundColor: "hsl(38 92% 50% / 0.08)" }}
        >
          <AlertTriangle className="h-3 w-3 flex-shrink-0" style={{ color: "hsl(var(--warning))" }} />
          <span className="text-muted-foreground">
            Weight higher than usual. Double-check before posting.
          </span>
        </div>
      )}
    </div>
  );
}
